// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useMemo } from 'react';
import { AddOutlined, Close } from '@mui/icons-material';
import { Box, Button, IconButton, TextField } from '@mui/material';
import { Select, SelectOptionProps } from '../../../../components/Form/Select';
import { Instrument, InstrumentKey } from '../../../../utils/instrument';
import { isPositiveFloat } from '../../../../utils/isPositiveFloat';
import { MarketData, PricingData } from './useMarketData';

type OnAddPricingData = () => void;
type OnUpdatePricingData = (
  uuid: string,
  args: {
    instrument?: InstrumentKey | '';
    tag?: string;
    value?: string;
  },
) => void;
type OnRemovePricingData = (uuid: string) => void;

const InstrumentOption = ({ option }: SelectOptionProps<Instrument>) => {
  return <Box>{option.displayName}</Box>;
};

type PricingDataInputProps = {
  value: PricingData;
  onUpdate: OnUpdatePricingData;
  onRemove: OnRemovePricingData;
  selectedInstruments: (InstrumentKey | '')[];
  eligibleInstruments: Instrument[];
};

const PricingDataInput = ({
  value,
  onUpdate,
  onRemove,
  selectedInstruments,
  eligibleInstruments,
}: PricingDataInputProps) => {
  const selectedKeys = useMemo(() => {
    return new Set(value.instrument ? [value.instrument] : []);
  }, [value]);

  const instruments = useMemo(() => {
    return eligibleInstruments.filter(({ key }) => {
      return !selectedInstruments.includes(key) || value.instrument === key;
    });
  }, [value, selectedInstruments, eligibleInstruments]);

  const keyToInstrument = useMemo(() => {
    return new Map(eligibleInstruments.map((instrument) => [instrument.key, instrument]));
  }, [eligibleInstruments]);

  const selectLabel = useMemo(() => {
    const instrument = value.instrument && keyToInstrument.get(value.instrument);

    if (!instrument) {
      return 'Select instrument';
    }

    return instrument.displayName;
  }, [value, keyToInstrument]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
      <Box sx={{ flex: '3 1 0' }}>
        <Select
          label={selectLabel}
          options={instruments}
          getOptionKey={(inst) => inst.key}
          selectedKeys={selectedKeys}
          onOptionClick={(key) => {
            onUpdate(value.uuid, { instrument: key as InstrumentKey });
          }}
          Option={InstrumentOption}
          disabled={instruments.length === 0}
        />
      </Box>
      <Box sx={{ flex: '2 1 0' }}>
        <TextField
          fullWidth
          value={value.tag}
          onChange={(event) => {
            onUpdate(value.uuid, { tag: event.target.value });
          }}
          size="small"
          placeholder="Tag"
        />
      </Box>
      <Box sx={{ flex: '2 1 0' }}>
        <TextField
          value={value.value}
          onChange={(event) => {
            if (isPositiveFloat(event.target.value)) {
              onUpdate(value.uuid, { value: event.target.value });
            }
          }}
          size="small"
          placeholder="Value"
        />
      </Box>
      <Box sx={{ flex: '0 1 0' }}>
        <IconButton size="small" onClick={() => onRemove(value.uuid)}>
          <Close fontSize="small" color="action" />
        </IconButton>
      </Box>
    </Box>
  );
};

export type MarketDataFormProps = {
  value: MarketData;
  onAdd: OnAddPricingData;
  onUpdate: OnUpdatePricingData;
  onRemove: OnRemovePricingData;
  eligibleInstruments: Instrument[];
};

export const MarketDataForm = ({
  value,
  onAdd,
  onUpdate,
  onRemove,
  eligibleInstruments,
}: MarketDataFormProps) => {
  const selectedInstruments = useMemo(() => {
    return value.map((pricingData) => pricingData.instrument);
  }, [value]);

  const showAddPricingDataButton = value.length < eligibleInstruments.length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: 1 }}>
      {value.map((pricingData) => (
        <PricingDataInput
          key={pricingData.uuid}
          value={pricingData}
          onUpdate={onUpdate}
          onRemove={onRemove}
          selectedInstruments={selectedInstruments}
          eligibleInstruments={eligibleInstruments}
        />
      ))}
      {showAddPricingDataButton && (
        <Button
          sx={{ ml: 'auto' }}
          startIcon={<AddOutlined />}
          size="small"
          variant="text"
          onClick={onAdd}
        >
          Add Pricing data
        </Button>
      )}
    </Box>
  );
};
